import { MonitorStatus } from '../../types/prompt/MonitorStatusType';
import { ProjectStatus } from '../../types/projects/ProjectStatus';

/**
 * The allocation rule as the panel reads it (the backend's `assertPromptsFit` decides). A project with
 * an allocation may not take more prompts than it; a prompt the Agency Hub's project pause parked only
 * comes back with the project's Start in the hub. Data only, never the addon: an allocation is set
 * exactly on the accounts that have the hub.
 */

/** Statuses that are not in use against the allocation — the backend's `notInUseStatuses` with PAUSE_MODE (every hub account has it); the list already reads TO_PAUSE as PAUSED. */
const NOT_IN_USE: MonitorStatus[] = ['PAUSED', 'DELETED', 'TO_DELETE'];

export interface PromptLike {
  status: MonitorStatus;
  pausedBy?: 'USER' | 'SYSTEM' | 'PROJECT' | null;
}

export interface AllocatedProjectLike {
  status: ProjectStatus;
  promptAllocation: number | null;
}

/** Prompts in use (the allocation's "used"): everything but paused and deleted. */
export const promptsInUse = (prompts: ReadonlyArray<PromptLike>): number =>
  prompts.filter((prompt) => !NOT_IN_USE.includes(prompt.status)).length;

/** Room left in the project's allocation; null = no allocation (a project without a limit, with or without the hub). */
export const allocationRoom = (
  project: AllocatedProjectLike | null | undefined,
  used: number
): number | null =>
  project && project.promptAllocation !== null ? Math.max(0, project.promptAllocation - used) : null;

/** A prompt the hub's project pause parked, in a project still paused: no prompt-by-prompt resume. */
export const isParkedByHub = (
  project: AllocatedProjectLike | null | undefined,
  prompt: PromptLike
): boolean => project?.status === 'PAUSED' && prompt.pausedBy === 'PROJECT';

export type PromptFitRejection =
  | {
      kind: 'ALLOCATION_EXCEEDED';
      projectId: number;
      allocation: number;
      used: number;
      requested: number;
    }
  | { kind: 'PROJECT_PAUSED'; projectId: number };

/** The rule's typed 400s (`ALLOCATION_EXCEEDED`, `PROJECT_PAUSED`) out of an axios error; null for anything else. */
export const promptFitRejection = (error: unknown): PromptFitRejection | null => {
  const data = (error as { response?: { data?: Record<string, unknown> } } | null)?.response?.data;
  if (!data || typeof data.projectId !== 'number') {
    return null;
  }
  if (data.error === 'ALLOCATION_EXCEEDED') {
    return {
      kind: 'ALLOCATION_EXCEEDED',
      projectId: data.projectId,
      allocation: Number(data.allocation),
      used: Number(data.used),
      requested: Number(data.requested),
    };
  }
  if (data.error === 'PROJECT_PAUSED') {
    return { kind: 'PROJECT_PAUSED', projectId: data.projectId };
  }
  return null;
};
